// Dimensions and labels of the screw scene in the lock calculator's style (as its Quotes): linear dimensions with
// arrows and chip labels, force labels on their own red chip. The commands come from the shared scene builder,
// already in world coordinates (metres).
import { memo, useMemo, type FC } from "react";
import type { DimCommand } from "../tc3dViewer";
import LinearDimension from "./LinearDimension";
import QuoteLabel from "./QuoteLabel";
import { QUOTE, QUOTE_COLORS } from "./viewerConfig";

export type ScrewDimensionsProps = {
  commands: readonly DimCommand[];
  showLines: boolean;
  showForces: boolean;
};

// In the interactive viewer the side-only thickness chain (built for the report's side section) is not drawn: it
// would pile up on the front view. Force labels follow the Forces toggle; dimension lines follow "Mostra quote".
const isVisibleCommand = (command: DimCommand, showLines: boolean, showForces: boolean) => {
  if (command.kind === "line") return showLines && !command.opts?.only;
  return !command.force || showForces;
};

const ScrewDimensions: FC<ScrewDimensionsProps> = ({ commands, showLines, showForces }) => {
  const visible = useMemo(
    () => commands.filter((command) => isVisibleCommand(command, showLines, showForces)),
    [commands, showLines, showForces],
  );
  return (
    <>
      {visible.map((command, index) =>
        command.kind === "line" ? (
          <LinearDimension key={index} command={command} />
        ) : command.force ? (
          <QuoteLabel
            key={index}
            position={[command.x, command.y, command.z]}
            text={command.text}
            textColor={QUOTE_COLORS.forceLabelText}
            backgroundColor={QUOTE_COLORS.forceLabelBackground}
            size={QUOTE.forceLabelSize}
            awayFrom={command.force.away}
          />
        ) : (
          <QuoteLabel key={index} position={[command.x, command.y, command.z]} text={command.text} />
        ),
      )}
    </>
  );
};

export default memo(ScrewDimensions);
